import { sceneObjectFactoryVariables } from "../layout/scene-object-types.js";
import type { BehaviorNode } from "../blueprint/types.js";
import {
  FUNCTION_FLOW_OUT_PORT_ID,
  isFunctionGraphPublishable,
  MAX_FUNCTION_CALL_DEPTH,
  resolveFunctionOutputPorts,
} from "../blueprint/function-graph.js";
import { compileFlowFrom } from "./compileFlow.js";
import {
  isVariableGraphPublishable,
  MAX_VARIABLE_EVAL_DEPTH,
  VARIABLE_VALUE_PORT_ID,
} from "../blueprint/variable-graph.js";
import type {
  SceneBehaviorCatalog,
  SceneFunctionEntry,
} from "../layout/types.js";
import { createValueCompiler } from "./compileValue.js";
import { indexGraph } from "./graph.js";
import { compileSceneAnimations } from "./compileSceneAnimations.js";
import { compileSceneCustomEvents } from "./compileSceneCustomEvents.js";
import { mergeCompiledDependencies, uniqueDependencies } from "./method.js";
import { hasRuntimeVariable, readRuntimeVariable } from "./runtimeVariables.js";
import { buildDependencySignature } from "./renderCache.js";
import { stableValueKey } from "./method.js";
import type {
  CompiledFlow,
  CompiledFunctionRuntime,
  CompiledValue,
  FlowRunResult,
  RuntimeAnimationResolver,
  RuntimeContext,
  RuntimeCustomEventTrigger,
  RuntimeFunctionInputFrame,
  RuntimeVariableResolver,
  SceneCatalogDependencyIndex,
} from "./types.js";

export type CompiledSceneCatalog = {
  dependencies: string[];
  variables: Record<string, RuntimeVariableResolver>;
  functions: Record<string, CompiledFunctionRuntime>;
  animations: Record<string, RuntimeAnimationResolver>;
  animationsByName: Record<string, RuntimeAnimationResolver>;
  customEventTriggers: Record<string, RuntimeCustomEventTrigger>;
  customEventTriggersByName: Record<string, RuntimeCustomEventTrigger>;
  dependencyIndex: SceneCatalogDependencyIndex;
};

function findControlNodeId(
  nodesById: Map<string, BehaviorNode>,
  controlType: "variableEnd" | "functionEnd" | "functionBegin",
): string | undefined {
  for (const [nodeId, node] of nodesById) {
    if (node.type === "control" && node.controlType === controlType) {
      return nodeId;
    }
  }
  return undefined;
}

function isVariableEvalBlocked(
  evalStack: string[] | undefined,
  variableId: string,
): boolean {
  const stack = evalStack ?? [];
  if (stack.includes(variableId)) {
    return true;
  }
  return stack.length >= MAX_VARIABLE_EVAL_DEPTH;
}

function isFunctionEvalBlocked(
  evalStack: string[] | undefined,
  functionId: string,
): boolean {
  const stack = evalStack ?? [];
  if (stack.includes(functionId)) {
    return true;
  }
  return stack.length >= MAX_FUNCTION_CALL_DEPTH;
}

function compileVariableGraphOutput(
  graph: SceneBehaviorCatalog["variables"][number]["graph"],
  catalogDeps: SceneCatalogDependencyIndex,
): CompiledValue | null {
  if (!isVariableGraphPublishable(graph)) {
    return null;
  }

  const indexed = indexGraph(graph);
  const endNodeId = findControlNodeId(indexed.nodesById, "variableEnd");
  if (!endNodeId) {
    return null;
  }

  const valueCompiler = createValueCompiler(indexed, catalogDeps);
  return valueCompiler.compileInputValue(endNodeId, VARIABLE_VALUE_PORT_ID);
}

function compileFunctionGraphOutputs(
  entry: SceneFunctionEntry,
  catalogDeps: SceneCatalogDependencyIndex,
): Record<string, CompiledValue> | null {
  if (!isFunctionGraphPublishable(entry.graph)) {
    return null;
  }

  const indexed = indexGraph(entry.graph);
  const endNodeId = findControlNodeId(indexed.nodesById, "functionEnd");
  if (!endNodeId) {
    return null;
  }

  const valueCompiler = createValueCompiler(indexed, catalogDeps);
  const outputs: Record<string, CompiledValue> = {};

  for (const port of resolveFunctionOutputPorts(entry)) {
    if (port.valueType === "flow") {
      continue;
    }
    outputs[port.id] = valueCompiler.compileInputValue(endNodeId, port.id);
  }

  return outputs;
}

function compileFunctionBodyFlow(
  entry: SceneFunctionEntry,
  catalogDeps: SceneCatalogDependencyIndex,
): ((patchOwnerNodeId: string) => CompiledFlow) | null {
  if (!isFunctionGraphPublishable(entry.graph)) {
    return null;
  }

  const indexed = indexGraph(entry.graph);
  const beginNodeId = findControlNodeId(indexed.nodesById, "functionBegin");
  if (!beginNodeId) {
    return null;
  }

  return (patchOwnerNodeId: string) =>
    compileFlowFrom(
      indexed,
      patchOwnerNodeId,
      beginNodeId,
      FUNCTION_FLOW_OUT_PORT_ID,
      catalogDeps,
      { functionId: entry.id },
    );
}

function runFunctionFlow(
  entry: SceneFunctionEntry,
  bodyFlowFactory: NonNullable<ReturnType<typeof compileFunctionBodyFlow>>,
  ctx: RuntimeContext,
  args: RuntimeFunctionInputFrame,
  patchOwnerNodeId: string,
): FlowRunResult {
  if (isFunctionEvalBlocked(ctx.__functionEvalStack, entry.id)) {
    return { patches: [], effects: [], continuations: [] };
  }

  const childCtx: RuntimeContext = {
    ...ctx,
    __functionEvalStack: [...(ctx.__functionEvalStack ?? []), entry.id],
    __functionCallFrameStack: [...(ctx.__functionCallFrameStack ?? []), args],
  };

  return bodyFlowFactory(patchOwnerNodeId).run(childCtx);
}

function resumeFunctionFlow(
  entry: SceneFunctionEntry,
  bodyFlowFactory: NonNullable<ReturnType<typeof compileFunctionBodyFlow>>,
  ctx: RuntimeContext,
  continuation: Parameters<
    NonNullable<CompiledFunctionRuntime["resumeFlow"]>
  >[1],
): FlowRunResult {
  const resumeTarget = continuation.resumeTarget;
  if (
    resumeTarget?.type !== "function" ||
    resumeTarget.functionId !== entry.id
  ) {
    return { patches: [], effects: [], continuations: [] };
  }

  return bodyFlowFactory(resumeTarget.patchOwnerNodeId).run(ctx, {
    resumeOnly: true,
  });
}

export function compileSceneCatalog(
  catalog: SceneBehaviorCatalog | undefined,
): CompiledSceneCatalog {
  const variables: Record<string, RuntimeVariableResolver> = {};
  const functions: Record<string, CompiledFunctionRuntime> = {};
  const dependencyBuckets: string[][] = [];
  const dependencyIndex: SceneCatalogDependencyIndex = {
    variableDependenciesById: {},
    functionDependenciesById: {},
  };

  const variableEntries = [
    ...(catalog?.variables ?? []),
    ...sceneObjectFactoryVariables(catalog),
  ];
  const functionEntries = catalog?.functions ?? [];
  const catalogPassCount = Math.max(
    1,
    variableEntries.length + functionEntries.length,
  );

  for (let pass = 0; pass < catalogPassCount; pass += 1) {
    for (const entry of variableEntries) {
      const compiledOutput = compileVariableGraphOutput(
        entry.graph,
        dependencyIndex,
      );
      dependencyIndex.variableDependenciesById[entry.id] =
        compiledOutput?.dependencies ?? [];
    }

    for (const entry of functionEntries) {
      const compiledOutputs = compileFunctionGraphOutputs(
        entry,
        dependencyIndex,
      );
      const bodyFlowFactory = compileFunctionBodyFlow(entry, dependencyIndex);
      dependencyIndex.functionDependenciesById[entry.id] = uniqueDependencies([
        ...(compiledOutputs
          ? mergeCompiledDependencies(...Object.values(compiledOutputs))
          : []),
        ...(bodyFlowFactory ? bodyFlowFactory(entry.id).dependencies : []),
      ]);
    }
  }

  for (const entry of variableEntries) {
    const compiledOutput = compileVariableGraphOutput(
      entry.graph,
      dependencyIndex,
    );

    if (!compiledOutput) {
      variables[entry.id] = () => undefined;
      dependencyIndex.variableDependenciesById[entry.id] = [];
      continue;
    }

    dependencyBuckets.push(compiledOutput.dependencies);

    variables[entry.id] = (ctx, args) => {
      if (hasRuntimeVariable(ctx, entry.id)) {
        return readRuntimeVariable(ctx, entry.id);
      }

      if (isVariableEvalBlocked(ctx.__variableEvalStack, entry.id)) {
        return undefined;
      }

      const signature = ctx.__sceneVariableCache
        ? buildDependencySignature(ctx, compiledOutput.dependencies) +
          "|args:" +
          stableValueKey(args)
        : undefined;
      let cache = ctx.__sceneVariableCache?.get(entry.id);
      if (signature !== undefined && cache?.has(signature))
        return cache.get(signature);

      const childCtx: RuntimeContext = {
        ...ctx,
        __variables: ctx.__variables ?? variables,
        __functions: ctx.__functions ?? functions,
        __variableEvalStack: [...(ctx.__variableEvalStack ?? []), entry.id],
        __variableInputFrameStack: [
          ...(ctx.__variableInputFrameStack ?? []),
          args,
        ],
      };

      const value = compiledOutput.evaluate(childCtx);
      if (signature !== undefined && ctx.__sceneVariableCache) {
        if (!cache) {
          cache = new Map();
          ctx.__sceneVariableCache.set(entry.id, cache);
        }
        // Bound memory even for a malformed/very large input list.
        if (cache.size >= 4096) cache.clear();
        cache.set(signature, value);
      }
      return value;
    };
  }

  for (const entry of functionEntries) {
    const compiledOutputs = compileFunctionGraphOutputs(entry, dependencyIndex);
    const bodyFlowFactory = compileFunctionBodyFlow(entry, dependencyIndex);

    if (!compiledOutputs && !bodyFlowFactory) {
      functions[entry.id] = {
        evaluateOutput: () => undefined,
      };
      continue;
    }

    if (compiledOutputs) {
      dependencyBuckets.push(
        mergeCompiledDependencies(...Object.values(compiledOutputs)),
      );
    }

    if (bodyFlowFactory) {
      const sampleFlow = bodyFlowFactory(entry.id);
      dependencyBuckets.push(sampleFlow.dependencies);
    }

    functions[entry.id] = {
      evaluateOutput(ctx, args, outputPortId) {
        if (!compiledOutputs) {
          return undefined;
        }

        if (isFunctionEvalBlocked(ctx.__functionEvalStack, entry.id)) {
          return undefined;
        }

        const compiledOutput = compiledOutputs[outputPortId];
        if (!compiledOutput) {
          return undefined;
        }

        const childCtx: RuntimeContext = {
          ...ctx,
          __variables: ctx.__variables ?? variables,
          __functions: ctx.__functions ?? functions,
          __functionEvalStack: [...(ctx.__functionEvalStack ?? []), entry.id],
          __functionCallFrameStack: [
            ...(ctx.__functionCallFrameStack ?? []),
            args,
          ],
        };

        return compiledOutput.evaluate(childCtx);
      },

      runFlow: bodyFlowFactory
        ? (ctx, args, patchOwnerNodeId) =>
            runFunctionFlow(
              entry,
              bodyFlowFactory,
              {
                ...ctx,
                __variables: ctx.__variables ?? variables,
                __functions: ctx.__functions ?? functions,
              },
              args,
              patchOwnerNodeId,
            )
        : undefined,
      resumeFlow: bodyFlowFactory
        ? (ctx, continuation) =>
            resumeFunctionFlow(entry, bodyFlowFactory, ctx, continuation)
        : undefined,
    };
  }

  const compiledCustomEvents = compileSceneCustomEvents(
    catalog?.customEvents ?? [],
  );
  const compiledAnimations = compileSceneAnimations(catalog?.animations ?? []);

  return {
    dependencies: uniqueDependencies([
      ...dependencyBuckets.flat(),
      ...compiledCustomEvents.dependencies,
      ...compiledAnimations.dependencies,
    ]),
    variables,
    functions,
    animations: compiledAnimations.animations,
    animationsByName: compiledAnimations.animationsByName,
    customEventTriggers: compiledCustomEvents.customEventTriggers,
    customEventTriggersByName: compiledCustomEvents.customEventTriggersByName,
    dependencyIndex,
  };
}
